import React, { useEffect, useState } from 'react';
import { Badge } from '../components/Badge';
import { SidebarView } from '../components/Sidebar';
import { cx } from '../components/styles';
import { useV2Translation } from '../i18n';
import { NavSectionView } from '../useV2Nav';
import {
  MOCK_EVENTS,
  MOCK_NAV,
  MOCK_TRANSFERS,
  MOCK_WAITLIST_EVENT,
} from './mockData';
import { useMediaQuery } from './Reveal';

/**
 * The interface, above the fold (DESIGN_PLAN §1.4).
 *
 * A non-interactive, hardcoded-data render of the real v2 shell — the actual
 * `SidebarView` fed a static nav array, not a screenshot. It fires no request:
 * `SidebarView` reads no hooks, and the cover art is a CSS gradient.
 *
 * It cycles three panels on a 4500 ms interval, crossfading with the existing
 * `fadeIn` keyframe. `aria-hidden` because it is decoration — the hero copy
 * carries the meaning. Frozen on panel 1 under `prefers-reduced-motion`, and
 * below `md` the sidebar is dropped and the interval never starts: it is a
 * needless main-thread cost on the slowest devices.
 */

const PANEL_COUNT = 3;
const PANEL_MS = 4500;

const CoverArt: React.FC<{ art: string; size: 'sm' | 'md' }> = ({ art, size }) => (
  <div
    className={cx(
      'flex-shrink-0 rounded-lg bg-gradient-to-br',
      art,
      size === 'md' ? 'h-16 w-16' : 'h-12 w-12',
    )}
  />
);

/** Panel 1 — the single most screenshot-worthy surface in the product. */
export const MockInventoryPanel: React.FC = () => (
  <div className="space-y-4">
    <div>
      <p className="text-lg font-bold text-white">Inventory</p>
      <p className="text-xs text-white/40">Manage your ticket inventory across platforms</p>
    </div>
    <div className="grid grid-cols-4 gap-2">
      {[
        { label: 'Total', value: '12', tone: 'text-white' },
        { label: 'Active', value: '9', tone: 'text-emerald-400' },
        { label: 'Transferred', value: '2', tone: 'text-blue-400' },
        { label: 'Listed', value: '1', tone: 'text-amber-400' },
      ].map((tile) => (
        <div key={tile.label} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
          <p className="text-[9px] uppercase tracking-wider text-white/40">{tile.label}</p>
          <p className={cx('text-lg font-semibold tabular-nums', tile.tone)}>{tile.value}</p>
        </div>
      ))}
    </div>
    <div className="space-y-2">
      {MOCK_EVENTS.map((event) => (
        <div
          key={event.id}
          className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3"
        >
          <CoverArt art={event.art} size="md" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-white">{event.name}</p>
            <p className="text-xs text-white/40">
              {event.when} · {event.venue}
            </p>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {event.types.map((type) => (
                <Badge key={type.label} size="sm" tone={type.transferred ? 'blue' : 'neutral'}>
                  {type.label}
                </Badge>
              ))}
              {event.accounts.map((account) => (
                <span
                  key={account}
                  className="rounded bg-white/[0.04] px-1.5 py-0.5 font-mono text-[9px] text-white/35"
                >
                  📱 {account}
                </span>
              ))}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className="min-w-[48px] text-right text-sm font-semibold tabular-nums text-white">
              {event.count}
            </span>
            <span className="rounded-lg bg-[#3b82f6] px-2.5 py-1 text-[10px] font-medium text-white">
              {event.transferLabel}
            </span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

/** Panel 2 — Waitlists. */
const MockWaitlistPanel: React.FC = () => (
  <div className="space-y-4">
    <div>
      <p className="text-lg font-bold text-white">Waitlist</p>
      <p className="text-xs text-white/40">3 events</p>
    </div>
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
      <div className="flex items-start gap-3">
        <CoverArt art={MOCK_WAITLIST_EVENT.art} size="md" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-white">{MOCK_WAITLIST_EVENT.name}</p>
          <p className="text-xs text-white/40">
            {MOCK_WAITLIST_EVENT.when} · {MOCK_WAITLIST_EVENT.venue} · {MOCK_WAITLIST_EVENT.city}
          </p>
        </div>
        <Badge tone="red" size="sm">
          Sold Out
        </Badge>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        {MOCK_WAITLIST_EVENT.types.map((type) => (
          <div
            key={type.name}
            className={cx(
              'rounded-lg border p-2.5',
              type.open
                ? 'border-[#3b82f6] bg-[#3b82f6]/10'
                : 'border-white/[0.06] bg-white/[0.02] opacity-60',
            )}
          >
            <p className="truncate text-[11px] font-medium text-white">{type.name}</p>
            <p className="text-[11px] tabular-nums text-white/50">{type.price}</p>
            <div className="mt-1.5">
              <Badge tone={type.open ? 'green' : 'red'} size="sm">
                {type.open ? 'Waitlist Open' : 'Sold Out'}
              </Badge>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-white/[0.06] pt-4">
        <div className="flex rounded-lg border border-white/10 bg-white/[0.03] p-1">
          <span className="rounded-md bg-[#3b82f6]/15 px-2.5 py-1 text-[10px] font-medium text-[#60a5fa]">
            All Accounts (6)
          </span>
          <span className="px-2.5 py-1 text-[10px] font-medium text-white/50">Select Accounts</span>
        </div>
        <span className="rounded-lg bg-[#3b82f6] px-3 py-1.5 text-[11px] font-medium text-white">
          {MOCK_WAITLIST_EVENT.joinLabel}
        </span>
      </div>
    </div>
  </div>
);

/** Panel 3 — Transfers. */
const MockTransfersPanel: React.FC = () => (
  <div className="space-y-4">
    <div>
      <p className="text-lg font-bold text-white">Transfer log</p>
      <p className="text-xs text-white/40">4 transfers</p>
    </div>
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02]">
      <div className="grid grid-cols-12 gap-3 border-b border-white/[0.06] px-3 py-2 text-[9px] uppercase tracking-wider text-white/35">
        <span className="col-span-3">Source</span>
        <span className="col-span-2">Status</span>
        <span className="col-span-1">When</span>
        <span className="col-span-3">Event</span>
        <span className="col-span-2">Recipient</span>
        <span className="col-span-1 text-right">Qty</span>
      </div>
      {MOCK_TRANSFERS.map((row) => (
        <div
          key={row.id}
          className="grid grid-cols-12 items-center gap-3 border-b border-white/[0.05] px-3 py-2.5 last:border-b-0"
        >
          <span className="col-span-3 min-w-0">
            <Badge tone={row.sourceTone} size="sm">
              {row.source}
            </Badge>
          </span>
          <span className="col-span-2 min-w-0">
            <Badge tone={row.statusTone} size="sm">
              {row.status}
            </Badge>
          </span>
          <span className="col-span-1 truncate text-[11px] text-white/40">{row.when}</span>
          <span className="col-span-3 truncate text-xs text-white/70">{row.event}</span>
          <span className="col-span-2 truncate text-[11px] text-white/50">{row.recipient}</span>
          <span className="col-span-1 text-right text-[11px] tabular-nums text-white/60">
            {row.qty}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const PANELS = [MockInventoryPanel, MockWaitlistPanel, MockTransfersPanel];
/** The sidebar's active item moves with the panel. */
const PANEL_ACTIVE_IDS = ['tickets', 'waitlist', 'transfers'];

export const ProductMock: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useV2Translation();
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const isMdUp = useMediaQuery('(min-width: 768px)');
  const [panel, setPanel] = useState(0);

  const frozen = reducedMotion || !isMdUp;

  useEffect(() => {
    if (frozen) {
      setPanel(0);
      return;
    }
    const id = window.setInterval(() => setPanel((p) => (p + 1) % PANEL_COUNT), PANEL_MS);
    return () => window.clearInterval(id);
  }, [frozen]);

  const activeId = PANEL_ACTIVE_IDS[panel];
  const sections: NavSectionView[] = MOCK_NAV.map((section) => ({
    id: section.id,
    label: t(section.labelKey),
    items: section.items.map((item) => ({
      id: item.id,
      path: item.path,
      label: t(item.labelKey),
      icon: item.icon,
      locked: false,
    })),
  }));
  const activePath =
    MOCK_NAV.flatMap((s) => s.items).find((i) => i.id === activeId)?.path ?? '';

  const Panel = PANELS[panel];

  return (
    <div
      aria-hidden="true"
      className={cx(
        'overflow-hidden rounded-2xl border border-white/10 bg-[#0f0f0f] shadow-2xl shadow-black/60',
        className,
      )}
    >
      {/* Browser chrome */}
      <div className="flex h-8 items-center gap-2 border-b border-white/[0.06] px-3">
        <span className="h-2.5 w-2.5 rounded-full bg-white/10" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/10" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/10" />
        <span className="mx-auto rounded-full bg-white/[0.04] px-3 py-0.5 text-[10px] text-white/30">
          flashtools.fm
        </span>
      </div>

      <div className="h-[380px] overflow-hidden md:h-[493px]">
        <div className="pointer-events-none flex w-[117.65%] origin-top-left scale-[0.85] select-none">
          {isMdUp && (
            <div className="h-[580px] w-64 flex-shrink-0 border-r border-white/[0.08]">
              <SidebarView
                sections={sections}
                navLabel={t('app.navigation.mainLabel')}
                searchLabel={t('app.navigation.search')}
                isStatic
                activePath={activePath}
              />
            </div>
          )}
          <div className="h-[580px] min-w-0 flex-1 overflow-hidden p-5">
            <div key={panel} className="v2-fade-in">
              <Panel />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
